import { useCallback, useEffect, useMemo, useRef } from 'react'

import './_notifications.scss'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import noNotifications from 'assets/images/no-notifications.png'
import { IconFilterOutline, IconSettingsOutline } from 'assets/svg/sprite'
import LoadingDots from 'components/UI/LoadingDots/LoadingDots'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import NotificationCard from 'features/NotificationCard/NotificationCard'
import { useFilterQuery, useNotificationsFeedInfinite } from 'helpers/hooks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { apiNotificationsAsRead } from 'services/endpoints/notification'

export const Notifications = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setTitle } = usePageTitle()

  // last-item sentinel for infinite scroll
  const { ref: lastNotificationRef, inView } = useInView({ threshold: 0.1 })

  // current filter taken from the URL (?type=...)
  const filter = useFilterQuery()
  const typeParam = filter.get('type') ?? 'all'

  // main query
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    status,
    refetchFirstPage,
    dataUpdatedAt,
  } = useNotificationsFeedInfinite(typeParam)

  // snapshot the timestamp we mounted with (used to detect a fresh fetch landing)
  const mountedUpdatedAtRef = useRef<number | null>(null)
  if (mountedUpdatedAtRef.current === null) {
    mountedUpdatedAtRef.current = dataUpdatedAt ?? 0
  }

  // derive notifications from pages without copying into local state
  const notifications = useMemo(() => {
    if (!data?.pages?.length) return []
    // shape: page.page.data.data
    return data.pages.flatMap((p: any) => p.page?.data?.data ?? [])
  }, [data])

  // status for empty vs. non-empty
  const hasNotifications = notifications.length > 0
  const isInitialLoading = status === 'loading' || (isFetching && !data)

  // set title once
  useEffect(() => {
    setTitle('Notifications')
  }, [setTitle])

  // Optional: refresh ONLY page 1 once on mount so we can mark-as-read after a fresh response
// Optional: refresh ONLY page 1 once on mount (rate-limited)
useEffect(() => {
  const COOLDOWN_MS = 15_000

  // Only attempt once we have something in cache OR we know query state
  // (status check keeps you from doing it while still loading)
  if (status !== 'success') return

  // If the query updated recently, skip
  const last = dataUpdatedAt ?? 0
  if (Date.now() - last < COOLDOWN_MS) return

  refetchFirstPage()
}, [status, dataUpdatedAt, refetchFirstPage])

  // Build a stable "cursor" for the newest item rendered (created_at + id)
  const newest = notifications[0] as { id?: string | number; created_at?: string } | undefined
  const newestCursor = useMemo(() => {
    return newest?.created_at && newest?.id != null
      ? `${newest.created_at}_${newest.id}`
      : null
  }, [newest])

  // capture the initial cursor per filter (from cache/first render)
  const initialCursorByTypeRef = useRef<Record<string, string | null>>({})
  if (!(typeParam in initialCursorByTypeRef.current)) {
    // record whatever we rendered at mount/time of first seeing this filter
    initialCursorByTypeRef.current[typeParam] = newestCursor ?? null
  }

  // Track last-marked cursor per filter to avoid repeated writes on identical data
  const lastMarkedCursorByTypeRef = useRef<Record<string, string>>({})

  // Mark notifications as read only when:
  //  - we have items
  //  - a network fetch completed after mount (dataUpdatedAt increased vs mount snapshot)
  //  - the newest cursor differs from BOTH:
  //      a) what we initially had (cache) and
  //      b) what we've already marked for this filter
  useEffect(() => {
    if (!hasNotifications || !newestCursor) return

    const fetchedAfterMount = (dataUpdatedAt ?? 0) > (mountedUpdatedAtRef.current ?? 0)
    if (!fetchedAfterMount) return

    const initialCursorForType = initialCursorByTypeRef.current[typeParam] ?? null
    if (initialCursorForType && newestCursor === initialCursorForType) {
      // Server returned the same newest item as cache -> nothing new, skip
      return
    }

    const prevMarked = lastMarkedCursorByTypeRef.current[typeParam]
    if (prevMarked === newestCursor) return

    lastMarkedCursorByTypeRef.current[typeParam] = newestCursor

    // fire-and-forget
    apiNotificationsAsRead()
    // update any small counters/badges
    queryClient.invalidateQueries(['pendingStats'])
  }, [hasNotifications, newestCursor, typeParam, dataUpdatedAt, queryClient])

  // infinite scroll trigger (guarded)
  useEffect(() => {
    if (!inView) return
    if (!hasNextPage) return
    if (isFetchingNextPage) return
    if (status !== 'success' || isFetching) return
    fetchNextPage()
  }, [inView, hasNextPage, isFetchingNextPage, status, isFetching, fetchNextPage])

  // Cancel notifications on Unmount (below)
  useEffect(() => {
    return () => {
      // cancel ALL notification queries regardless of filter
      queryClient.cancelQueries({ queryKey: ['notifications'] })
    }
  }, [queryClient])


  const filterDropdownItems = useMemo(
    () => [
      { value: 'all',           name: t('All') },
      { value: 'interactions',  name: t('interactions') },
      { value: 'likes',         name: t('likes') },
      { value: 'subscriptions', name: t('subscriptions') },
      { value: 'tips',          name: t('tips') },
      { value: 'messages',      name: t('messages') },
      { value: 'premium',       name: t('premiumContent') },
    ],
    [t]
  )

  const applyFilter = useCallback((val: string) => {
    if (val === 'all') {
      navigate({ pathname: '/notifications' })
    } else {
      navigate({ pathname: '/notifications', search: `?type=${val}` })
    }
    // optional: scroll to top on filter change, if needed
    // window.scrollTo(0, 0)
  }, [navigate])

  return (
    <div className='notificationPageLayout'>
      <WithHeaderSection
        customClass='notifications'
        headerSection={
          <LayoutHeader
            title={typeParam}
            type='title-with-buttons-no-icon'
            sortingProps={{
              selectedSort: typeParam,
              selectedOrder: ''
            }}
            buttonSize="small"
            buttons={[
              {
                type: 'settings',
                color: 'transparent',
                icon: <IconSettingsOutline color='#262A33' />,
                action: '/settings/notifications',
              },
              {
                type: 'filter',
                elements: { first_section: filterDropdownItems },
                color: 'transparent',
                icon: <IconFilterOutline />
              }
            ]}
            applyFn={(val: string) => applyFilter(val)}
          />
        }
      >
        {/* initial loader */}
        {isInitialLoading && !isFetchingNextPage && <div className="notificationsLoader"> <BeatLoader size={8}/></div>}

        {/* non-empty list */}
        {hasNotifications && (
          <div className='notificationsList'>
            {notifications.map((notification: any, index: number) => {
              const isLast = index === notifications.length - 1
              return (
                <div
                  key={notification.id}
                  ref={isLast ? lastNotificationRef : undefined}
                >
              <NotificationCard
                data={{
                  is_online: notification?.is_online,
                  is_liked: notification?.is_liked,
                  friendship_status: notification?.friendship_status,
                  subscription_status: notification?.subscription_status,
                  show_fan_options: notification?.show_fan_options,
                  can_like_notification: notification?.can_like_notification,
                  ...notification.data,
                }}
                type={notification.data?.notification_type}
                time={notification.created_at}
              />
                </div>
              )
            })}
            <LoadingDots visible={isFetchingNextPage} />
          </div>
        )}

        {/* empty state */}
        {!isInitialLoading && !hasNotifications && (
          <div className='notificationsList__empty'>
            <img src={noNotifications} alt='noNotifications' />
            <div className='notificationsList__empty__text'>
              <div className='notificationsList__empty__text--title'>
                {t('noNotifications')}
              </div>
              <div className='notificationsList__empty__text--sub'>
                {t('noNotifications')}
              </div>
            </div>
          </div>
        )}
      </WithHeaderSection>

      <DesktopAdditionalContent />
    </div>
  )
}

export default Notifications
